import React, { useState, useEffect, useCallback } from 'react';
import TimeRangeSelector from '../reports/TimeRangeSelector';
import MultiSelectDropdown from '../dropdown/MultiSelectDropdown';
import TimeSeriesChart from '../reports/TimeSeriesChart';
import usePagination from '../utils/usePagination';
import { formatLocalDateTime } from '../utils/formatDateTime';
import telemetryService, {
  TELEMETRY_METRIC_OPTIONS,
  TELEMETRY_RAT_OPTIONS,
} from '../../services/telemetryService';
import '../assets/AllTabsCommon.css';
import '../assets/Home.css';
import '../assets/RemoteDebugSessionTabs.css';

const SESSIONS_PER_PAGE = 10;
const TELEMETRY_ROWS_PER_PAGE = 10;

const TELEMETRY_COLUMNS = [
  { key: 'timestamp',  label: 'Timestamp' },
  { key: 'action',     label: 'Action' },
  { key: 'rat',        label: 'RAT' },
  { key: 'lteValue',   label: 'LTE Value' },
  { key: 'nr5gValue',  label: 'NR5G Value' },
  { key: 'ul',         label: 'UL (Mbps)' },
  { key: 'dl',         label: 'DL (Mbps)' },
  { key: 'rtt',        label: 'RTT (ms)' },
  { key: 'pci',        label: 'PCI' },
  { key: 'mccMnc',     label: 'MCC-MNC' },
  { key: 'error',      label: 'Error' },
];

function RTTHistory() {
  // ── Sessions list ──────────────────────────────────────────────────────────
  const [sessions, setSessions]         = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [searchTerm, setSearchTerm]     = useState('');
  const [currentPage, setCurrentPage]   = useState(1);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const data = await telemetryService.getCompletedSessionsForHistory();
      setSessions(Array.isArray(data) ? data : []);
    } catch (_) {
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // ── Expanded session (one at a time) ──────────────────────────────────────
  const [expandedId, setExpandedId] = useState(null);

  const handleRowClick = (sessionId) => {
    setExpandedId((prev) => (prev === sessionId ? null : sessionId));
  };

  // ── Search + pagination on session list ───────────────────────────────────
  const filteredSessions = sessions.filter((s) =>
    !searchTerm ||
    (s.systemId  || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.sessionId || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const { pageItems: sessionPage, renderPageNumbers } = usePagination(
    filteredSessions, currentPage, setCurrentPage, SESSIONS_PER_PAGE
  );

  return (
    <div className="data-table-tab-container">
      {/* ── Search bar ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div className="search-container" style={{ margin: 0, maxWidth: 320 }}>
          <input
            type="text"
            placeholder="Search by System ID or Session ID…"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
        </div>
        <button
          className="nd-refresh-btn"
          onClick={loadSessions}
          disabled={sessionsLoading}
          title="Refresh"
        >
          {sessionsLoading ? '⏳' : '↻'}
        </button>
      </div>

      {/* ── Sessions table ──────────────────────────────────────────────── */}
      {sessionsLoading ? (
        <div className="card rt-card">
          <p className="rt-text-muted" style={{ padding: '24px 0', textAlign: 'center' }}>
            Loading sessions…
          </p>
        </div>
      ) : filteredSessions.length === 0 ? (
        <div className="card rt-card">
          <p className="rt-text-muted" style={{ padding: '24px 0', textAlign: 'center' }}>
            {sessions.length === 0
              ? 'No completed sessions found.'
              : 'No sessions match your search.'}
          </p>
        </div>
      ) : (
        <>
          <div className="boot-kpi-table-container">
            <div className="boot-kpi-table-wrapper">
              <table className="boot-kpi-table">
                <thead>
                  <tr>
                    <th style={{ width: '28%' }}>System ID</th>
                    <th style={{ width: '32%' }}>Session ID</th>
                    <th style={{ width: '20%' }}>Started</th>
                    <th style={{ width: '10%' }}>Duration</th>
                    <th style={{ width: '10%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {sessionPage.map((s) => (
                    <React.Fragment key={s.sessionId}>
                      <tr
                        className={`nd-sv-row${expandedId === s.sessionId ? ' expanded-row' : ''}`}
                        onClick={() => handleRowClick(s.sessionId)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="boot-kpi-table-cell-kpi nd-system-id" title={s.systemId}>
                          {s.systemId}
                        </td>
                        <td className="boot-kpi-table-cell" style={{ fontSize: 12, color: '#475569' }} title={s.sessionId}>
                          {s.sessionId}
                        </td>
                        <td className="boot-kpi-table-cell" style={{ fontSize: 12, color: '#475569' }}>
                          {s.startTime ? formatLocalDateTime(s.startTime) : '—'}
                        </td>
                        <td className="boot-kpi-table-cell" style={{ fontSize: 12, color: '#475569' }}>
                          {s.durationMins ? `${s.durationMins} min` : '—'}
                        </td>
                        <td className="boot-kpi-table-cell" style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8' }}>
                          {expandedId === s.sessionId ? '▲' : '▼'}
                        </td>
                      </tr>

                      {expandedId === s.sessionId && (
                        <tr className="expanded-details-row">
                          <td colSpan={5}>
                            <div className="expanded-row-content">
                              <SessionTelemetry session={s} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {filteredSessions.length > SESSIONS_PER_PAGE && (
            <div className="pagination-controls">{renderPageNumbers()}</div>
          )}
        </>
      )}
    </div>
  );
}

// ── Inline telemetry detail panel (mounted when a row is expanded) ───────────

function SessionTelemetry({ session }) {
  const [timeRange, setTimeRange]           = useState('15m');
  const [selectedMetrics, setSelectedMetrics] = useState(TELEMETRY_METRIC_OPTIONS.map((o) => o.value));
  const [selectedRat, setSelectedRat]       = useState('all');

  const [signalSeries, setSignalSeries]         = useState([]);
  const [throughputSeries, setThroughputSeries] = useState([]);
  const [rows, setRows]                         = useState([]);
  const [loading, setLoading]                   = useState(false);

  const [rowSearch, setRowSearch]   = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await telemetryService.getHistory({
        sessionId:    session.sessionId,
        systemId:     session.systemId,
        timeRange,
        metrics:      selectedMetrics,
        rat:          selectedRat,
      });
      setSignalSeries(data.signalSeries || []);
      setThroughputSeries(data.throughputSeries || []);
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } finally {
      setLoading(false);
    }
  }, [session.sessionId, session.systemId, timeRange, selectedMetrics, selectedRat]);

  useEffect(() => { load(); }, [load]);

  // Metric label ↔ value maps
  const metricOptionStrings = TELEMETRY_METRIC_OPTIONS.map((o) => o.label);
  const metricValueToLabel  = Object.fromEntries(TELEMETRY_METRIC_OPTIONS.map((o) => [o.value, o.label]));
  const metricLabelToValue  = Object.fromEntries(TELEMETRY_METRIC_OPTIONS.map((o) => [o.label, o.value]));
  const selectedMetricLabels = selectedMetrics.map((v) => metricValueToLabel[v]).filter(Boolean);

  const ratOptionStrings = TELEMETRY_RAT_OPTIONS.map((o) => o.label);
  const ratValueToLabel  = Object.fromEntries(TELEMETRY_RAT_OPTIONS.map((o) => [o.value, o.label]));
  const ratLabelToValue  = Object.fromEntries(TELEMETRY_RAT_OPTIONS.map((o) => [o.label, o.value]));
  const selectedRatLabel = ratValueToLabel[selectedRat] || 'All RATs';

  const filtered = rows.filter((r) =>
    !rowSearch ||
    (r.action || '').toLowerCase().includes(rowSearch.toLowerCase()) ||
    (r.rat    || '').toLowerCase().includes(rowSearch.toLowerCase())
  );
  const { pageItems, renderPageNumbers } = usePagination(filtered, currentPage, setCurrentPage, TELEMETRY_ROWS_PER_PAGE);

  const ACTION_BADGE = {
    'RSRP':         'rtt-badge rtt-badge--blue',
    'RSRQ':         'rtt-badge rtt-badge--blue',
    'SINR':         'rtt-badge rtt-badge--blue',
    'Throughput':   'rtt-badge rtt-badge--purple',
    'Serving Cell': 'rtt-badge rtt-badge--green',
    'Neighbor Cell':'rtt-badge rtt-badge--amber',
  };

  const renderCell = (row, colKey) => {
    switch (colKey) {
      case 'timestamp': return formatLocalDateTime(row.timestamp);
      case 'action':    return (
        <span className={ACTION_BADGE[row.action] || 'rtt-badge rtt-badge--gray'}>
          {row.action}
        </span>
      );
      case 'ul':        return row.ul  != null ? row.ul  : '—';
      case 'dl':        return row.dl  != null ? row.dl  : '—';
      case 'rtt':       return row.rtt != null ? row.rtt : '—';
      case 'lteValue':  return row.lteValue  || '—';
      case 'nr5gValue': return row.nr5gValue || '—';
      case 'error':     return row.error
        ? <span className="rtt-badge rtt-badge--red">{row.error}</span>
        : '—';
      default: return row[colKey] != null ? row[colKey] : '—';
    }
  };

  return (
    <div style={{ padding: '16px 8px' }}>
      {/* ── Filters ───────────────────────────────────────────────────── */}
      <div className="rt-filter-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <MultiSelectDropdown
          placeholder="Metrics"
          options={metricOptionStrings}
          selected={selectedMetricLabels}
          onChange={(labels) => {
            setSelectedMetrics(labels.map((l) => metricLabelToValue[l]).filter(Boolean));
            setCurrentPage(1);
          }}
          entityName="Metrics"
        />
        <MultiSelectDropdown
          placeholder="RAT"
          options={ratOptionStrings}
          selected={[selectedRatLabel]}
          onChange={(labels) => {
            const last = labels.length > 0 ? labels[labels.length - 1] : 'All RATs';
            setSelectedRat(ratLabelToValue[last] || 'all');
            setCurrentPage(1);
          }}
          entityName="RAT"
        />
        <TimeRangeSelector
          selectedRange={timeRange}
          onRangeChange={(r) => { setTimeRange(r); setCurrentPage(1); }}
          preset="aggregate"
        />
      </div>

      {loading ? (
        <p className="rt-text-muted" style={{ padding: '16px 0', textAlign: 'center' }}>
          Loading telemetry…
        </p>
      ) : (
        <>
          {/* ── Charts ──────────────────────────────────────────────── */}
          {signalSeries.length > 0 && (
            <div className="card rt-card" style={{ marginBottom: 12 }}>
              <TimeSeriesChart
                title="Signal Strength (RSRP / RSRQ / SINR)"
                series={signalSeries}
                yAxisLabel="dBm / dB"
                timeRange={timeRange}
              />
            </div>
          )}
          {throughputSeries.length > 0 && (
            <div className="card rt-card" style={{ marginBottom: 12 }}>
              <TimeSeriesChart
                title="Throughput (UL / DL)"
                series={throughputSeries}
                yAxisLabel="Mbps"
                timeRange={timeRange}
              />
            </div>
          )}
          {signalSeries.length === 0 && throughputSeries.length === 0 && rows.length === 0 && (
            <p className="rt-text-muted" style={{ padding: '12px 0', textAlign: 'center' }}>
              No telemetry data for the selected filters.
            </p>
          )}

          {/* ── Raw telemetry table ──────────────────────────────────── */}
          {rows.length > 0 && (
            <div className="rtt-telem-card">
              <div className="rtt-telem-card-title">
                <span>Raw Telemetry Data</span>
                <div className="search-container" style={{ margin: 0, maxWidth: 220 }}>
                  <input
                    type="text"
                    placeholder="Search by action or RAT"
                    value={rowSearch}
                    onChange={(e) => { setRowSearch(e.target.value); setCurrentPage(1); }}
                  />
                </div>
              </div>
              <div className="rtt-telem-table-wrap">
                <table className="rtt-telem-table">
                  <colgroup>
                    <col style={{ width: '160px', minWidth: '150px' }} /> {/* timestamp */}
                    <col style={{ width: '120px', minWidth: '100px' }} /> {/* action */}
                    <col style={{ width: '80px'  }} /> {/* rat */}
                    <col style={{ width: '100px' }} /> {/* lteValue */}
                    <col style={{ width: '100px' }} /> {/* nr5gValue */}
                    <col style={{ width: '90px'  }} /> {/* ul */}
                    <col style={{ width: '90px'  }} /> {/* dl */}
                    <col style={{ width: '80px'  }} /> {/* rtt */}
                    <col style={{ width: '70px'  }} /> {/* pci */}
                    <col style={{ width: '90px'  }} /> {/* mccMnc */}
                    <col style={{ width: '80px'  }} /> {/* error */}
                  </colgroup>
                  <thead>
                    <tr>{TELEMETRY_COLUMNS.map((col) => <th key={col.key}>{col.label}</th>)}</tr>
                  </thead>
                  <tbody>
                    {pageItems.length === 0 ? (
                      <tr>
                        <td colSpan={TELEMETRY_COLUMNS.length} className="rtt-telem-empty">
                          No results match your search.
                        </td>
                      </tr>
                    ) : (
                      pageItems.map((row, idx) => (
                        <tr key={`${row.timestamp}-${idx}`}>
                          {TELEMETRY_COLUMNS.map((col) => (
                            <td key={col.key}>{renderCell(row, col.key)}</td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="pagination-controls">{renderPageNumbers()}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default RTTHistory;
